import { AbstractControl } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { booleanAttribute, Component, computed, HostBinding, inject, input, OnInit, output } from '@angular/core';

import { InputControlComponent } from '../input-control/input-control.component';
import { fieldAppearance, fieldColor, floatLabel, hintAlign, subscriptSizing, TFieldAppearance, TFieldColor, TFloatLabel, THintAlign, TOptionSortComparator, TSubscriptSizing } from '../../models/form-field-options';
import { formFieldSize, TFormFieldSize } from '../../models/form-field-size';
import { SharedModule } from '../../../shared.module';
import { SelectControlComponent } from '../select-control/select-control.component';

@Component({
  selector: 'app-select-control-with-readonly',
    templateUrl: './select-control-with-readonly.component.html',
    styleUrls: [
        '../../control.scss',
        './select-control-with-readonly.component.scss',
    ],
    standalone: true,
    imports: [
    SharedModule,
    InputControlComponent,
    SelectControlComponent
],
})
export class SelectControlWithReadonlyComponent
    implements OnInit
{
    // mat-form-field:
    input_appearance$$                   = input<TFieldAppearance>(fieldAppearance.fill,    { alias: 'appearance' });
    input_classHeight$$                  = input<TFormFieldSize>(formFieldSize.defHeight,   { alias: 'classHeight' });
    input_color$$                        = input<TFieldColor>(fieldColor.primary,           { alias: 'color' });
    input_floatLabel$$                   = input<TFloatLabel>(floatLabel.auto,              { alias: 'floatLabel' });
    input_hideLineRipple$$               = input<boolean>(false,                            { alias: 'hideLineRipple' });
    input_hideRequiredMarker$$           = input<boolean | undefined>(undefined,            { alias: 'hideRequiredMarker' });
    input_hint$$                         = input<string>('',                                { alias: 'hint' });
    input_hintAlign$$                    = input<THintAlign>(hintAlign.start,               { alias: 'hintAlign' });
    input_hintLabel$$                    = input<string>('',                                { alias: 'hintLabel' });
    input_label$$                        = input<string>('',                                { alias: 'label' });
    input_showError$$                    = input<boolean>(true,                             { alias: 'showError' });
    input_subscriptSizing$$              = input<TSubscriptSizing>(subscriptSizing.dynamic, { alias: 'subscriptSizing' });

    // mat-select:
    input_ariaLabel$$                    = input<string>('',                                                                            { alias: 'ariaLabel' });
    input_ariaLabelledBy$$               = input<string>('',                                                                            { alias: 'ariaLabelledBy' });
    input_autofocus$$                    = input<boolean>(true,                                                                         { alias: 'autofocus' });
    input_canSelectNullableOptions$$     = input(false,                                                                                 { transform: booleanAttribute, alias: 'canSelectNullableOptions' });
    input_compareWith$$                  = input<((o1: any, o2: any) => boolean) | null>(null,                                          { alias: 'compareWith' });
    input_control$$                      = input<AbstractControl | undefined>(undefined,                                                { alias: 'control' });
    input_controlName$$                  = input<string>('',                                                                            { alias: 'controlName' });
    input_disableOptionCentering$$       = input(false,                                                                                 { transform: booleanAttribute, alias: 'disableOptionCentering' });
    input_disableRipple$$                = input<boolean>(false,                                                                        { alias: 'disableRipple' });
    input_disabled$$                     = input(false,                                                                                 { transform: booleanAttribute, alias: 'disabled' });
    input_errorStateMatcher$$            = input<ErrorStateMatcher>(inject(ErrorStateMatcher),                                          { alias: 'errorStateMatcher' });
    input_hideSingleSelectionIndicator$$ = input<boolean>(true,                                                                         { alias: 'hideSingleSelectionIndicator' });
    input_id$$                           = input<string>('',                                                                            { alias: 'id' });
    input_multiple$$                     = input<boolean>(false,                                                                        { alias: 'multiple' });
    input_panelClass$$                   = input<string | string[] | Set<string> | { [key: string]: any }>('',                          { alias: 'panelClass' });
    input_panelWidth$$                   = input<string | number | null>('auto',                                                        { alias: 'panelWidth' });
    input_placeholder$$                  = input<string>('',                                                                            { alias: 'placeholder' });
    input_required$$                     = input(false,                                                                                 { transform: booleanAttribute, alias: 'required' });
    input_sortComparator$$               = input<TOptionSortComparator | undefined>(undefined,                                          { alias: 'sortComparator' });
    input_typeAheadDebounceInterval$$    = input<number>(0,                                                                             { alias: 'typeAheadDebounceInterval' });

    input_fieldName_display$$ = input<string>('',          { alias: 'fieldNameDisplay' });
    input_fieldName_value$$   = input<string>('',          { alias: 'fieldNameValue' });
    input_list$$              = input<any[]>([],           { alias: 'list' });
    input_readonly$$          = input<boolean>(false,      { alias: 'readonly' });
    input_value$$             = input<string | null>(null, { alias: 'value' });

    out_openedChange = output<boolean>({alias: 'openedChange'});
    out_selectionChange = output<any>({alias: 'selectionChange'});
    out_valueChange = output<any>({alias: 'valueChange'});
    
    value: string | null = null;

    @HostBinding('class.input-control') classInputControl = true;

    hideRequiredMarker$$ = computed(() =>
        this.input_hideRequiredMarker$$() ?? !this.input_required$$()
    );

    ngOnInit(): void {
        const __fieldNameDisplay = this.input_fieldName_display$$();
        const __control = this.input_control$$();
        const __value = this.input_value$$();
        this.value =  __control?.value && __fieldNameDisplay ? __control.value[__fieldNameDisplay] : __value;
    }

    emit_openedChange(value: boolean) {
        this.out_openedChange.emit(value);
    }

    emit_selectionChange(value: any) {
        this.out_selectionChange.emit(value);
    }

    emit_valueChange(value: any) {
        this.out_valueChange.emit(value);
    }
}
